import { Home, Sofa, Calendar } from "lucide-react";
import { useState } from "react";
import PropertyListingCard from "@/components/PropertyListingCard";
import { useProperties } from "@/hooks/useProperties";

const PropertiesForRent = () => {
  const [activeCategory, setActiveCategory] = useState("furnished");
  const { data: properties, isLoading } = useProperties({ listing_type: "rent", category: activeCategory, is_featured: true, limit: 6 });

  return (
    <section id="properties-for-rent" className="bg-brand-canvas py-16 md:py-24">
      <div className="px-6 md:px-16 max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <p className="font-open-sans font-semibold text-xs tracking-[0.3em] uppercase text-accent mb-6">Rental Solutions</p>
          <h2 className="font-open-sans font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-primary-foreground mb-8 leading-tight">Properties for Rent</h2>
          <div className="max-w-3xl mx-auto">
            <p className="font-open-sans text-lg leading-relaxed text-primary-foreground/75">
              From fully furnished executive apartments to customizable family homes, our rental portfolio offers premium living solutions.
            </p>
          </div>
        </div>

        <div className="flex justify-center mb-12">
          <div className="inline-flex rounded-lg border border-primary-foreground/15 bg-background/10 p-1 backdrop-blur-sm">
            <button onClick={() => setActiveCategory("furnished")} className={`px-6 py-3 font-open-sans font-semibold text-sm tracking-wider uppercase transition-all duration-300 rounded-md ${activeCategory === "furnished" ? "bg-accent text-accent-foreground" : "text-primary-foreground/70 hover:text-primary-foreground"}`}>Furnished</button>
            <button onClick={() => setActiveCategory("unfurnished")} className={`px-6 py-3 font-open-sans font-semibold text-sm tracking-wider uppercase transition-all duration-300 rounded-md ${activeCategory === "unfurnished" ? "bg-accent text-accent-foreground" : "text-primary-foreground/70 hover:text-primary-foreground"}`}>Unfurnished</button>
          </div>
        </div>

        {isLoading ? (
          <p className="text-center text-primary-foreground/70 font-open-sans">Loading properties...</p>
        ) : properties && properties.length > 0 ? (
          <div className="grid gap-8 md:grid-cols-3 mb-16">
            {properties.map((property) => (
              <PropertyListingCard key={property.id} {...property} statusLabel={property.status_label} categoryLabel={property.category_label} />
            ))}
          </div>
        ) : (
          <p className="text-center text-primary-foreground/70 font-open-sans mb-16">No rental properties available yet.</p>
        )}

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="rounded-xl border border-primary-foreground/10 bg-background/10 p-6 text-center backdrop-blur-sm">
            <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mx-auto mb-4"><Calendar className="w-6 h-6 text-accent" /></div>
            <h4 className="font-open-sans font-semibold text-sm text-primary-foreground mb-2">Flexible Terms</h4>
            <p className="font-open-sans text-xs text-primary-foreground/70">Short-term and long-term rental options</p>
          </div>
          <div className="rounded-xl border border-primary-foreground/10 bg-background/10 p-6 text-center backdrop-blur-sm">
            <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mx-auto mb-4"><Home className="w-6 h-6 text-accent" /></div>
            <h4 className="font-open-sans font-semibold text-sm text-primary-foreground mb-2">Prime Locations</h4>
            <p className="font-open-sans text-xs text-primary-foreground/70">Properties in Nairobi's best neighborhoods</p>
          </div>
          <div className="rounded-xl border border-primary-foreground/10 bg-background/10 p-6 text-center backdrop-blur-sm">
            <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mx-auto mb-4"><Sofa className="w-6 h-6 text-accent" /></div>
            <h4 className="font-open-sans font-semibold text-sm text-primary-foreground mb-2">Premium Amenities</h4>
            <p className="font-open-sans text-xs text-primary-foreground/70">Modern facilities and professional management</p>
          </div>
        </div>

        <div className="text-center">
          <a href="/properties-for-rent" className="inline-block font-open-sans font-bold text-xs tracking-[0.25em] uppercase bg-accent text-accent-foreground px-10 py-4 hover:bg-foreground hover:text-background transition-colors duration-300">
            View All {activeCategory === "furnished" ? "Furnished" : "Unfurnished"} Rentals
          </a>
        </div>
      </div>
    </section>
  );
};

export default PropertiesForRent;
